import { z } from 'zod';

/** The model families a job's steps choose from, in New Job's order (QZ-D170). Never remove one: stored jobs name them (QZ-D174). */
export const MODEL_FAMILIES = ['opus', 'sonnet', 'haiku', 'fable'] as const;
export const modelFamilySchema = z.enum(MODEL_FAMILIES);
export type ModelFamily = z.infer<typeof modelFamilySchema>;

/** The effort levels the Messages API accepts, lowest first (QZ-D170). */
export const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export const effortSchema = z.enum(EFFORTS);
export type Effort = z.infer<typeof effortSchema>;

/** The model id each family means today (QZ-D170). Every id must have a price in the worker. */
export const FAMILY_MODELS: Record<ModelFamily, string> = {
  opus: 'claude-opus-5-5',
  sonnet: 'claude-sonnet-5',
  haiku: 'claude-haiku-4-5',
  fable: 'claude-fable-5-1',
};

export const stepModelChoiceSchema = z
  .object({ family: modelFamilySchema, effort: effortSchema })
  .strict();
export type StepModelChoice = z.infer<typeof stepModelChoiceSchema>;
export const stepModelChoicesSchema = z
  .object({
    plan: stepModelChoiceSchema,
    write: stepModelChoiceSchema,
    check: stepModelChoiceSchema,
  })
  .strict();
export type StepModelChoices = z.infer<typeof stepModelChoicesSchema>;

/** A step's choice with the model id its family resolved to when the job was created (QZ-D174). The id is not checked against a list. */
export const jobStepModelSchema = stepModelChoiceSchema
  .extend({ model: z.string().min(1) })
  .strict();
export type JobStepModel = z.infer<typeof jobStepModelSchema>;
export const jobStepModelsSchema = z
  .object({ plan: jobStepModelSchema, write: jobStepModelSchema, check: jobStepModelSchema })
  .strict();
export type JobStepModels = z.infer<typeof jobStepModelsSchema>;

/** What a job runs on when it names no step models: QZ-D53's table (QZ-D174). */
export const DEFAULT_STEP_MODELS: StepModelChoices = {
  plan: { family: 'sonnet', effort: 'medium' },
  write: { family: 'opus', effort: 'high' },
  check: { family: 'sonnet', effort: 'high' },
};

/** Each step's choice with `FAMILY_MODELS[family]` as its model (QZ-D170). */
export const resolveStepModels = (choices: StepModelChoices): JobStepModels => ({
  plan: { ...choices.plan, model: FAMILY_MODELS[choices.plan.family] },
  write: { ...choices.write, model: FAMILY_MODELS[choices.write.family] },
  check: { ...choices.check, model: FAMILY_MODELS[choices.check.family] },
});
